import { useState } from 'react'
import { updateParametres, getParametres } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../components/ToastProvider'
import Icon from '../components/Icon'
import Logo, { Wordmark } from '../components/Logo'

/**
 * Les écrans annonçaient « scénarios », « objectifs financiers » et
 * « alertes budgétaires » : des fonctionnalités que l'application n'a pas.
 * Promettre au premier lancement ce qu'on ne tient pas est le plus sûr moyen
 * de décevoir ensuite. Le contenu décrit désormais ce qui existe vraiment.
 */
const STEPS = [
  {
    id: 'bienvenue',
    title: 'Bienvenue',
    lead: 'Finance Prévision répond à une question : combien me restera-t-il à la fin du mois ?',
    points: [
      { icon: 'bank', label: 'Vos comptes', text: 'Un ou plusieurs, avec leur solde et leur découvert autorisé.' },
      { icon: 'operations', label: 'Vos opérations', text: 'Entrées et dépenses, passées comme à venir.' },
      { icon: 'trendUp', label: 'Vos prévisions', text: 'Le solde que vous aurez, calculé à partir de ce que vous avez saisi.' },
    ],
  },
  {
    id: 'operations',
    title: 'Saisissez vos opérations',
    lead: 'Chaque opération porte un montant, une catégorie, une date et un compte.',
    points: [
      { icon: 'arrowDown', label: 'Une entrée', text: 'Augmente votre solde : salaire, remboursement, virement reçu.' },
      { icon: 'arrowUp', label: 'Une dépense', text: 'Le diminue : loyer, courses, abonnement.' },
      { icon: 'calendar', label: 'Une date future', text: 'L’opération est déjà comptée dans votre prévisionnel, pas dans le solde à ce jour.' },
    ],
  },
  {
    id: 'soldes',
    title: 'Deux soldes, deux usages',
    lead: 'C’est la distinction qui fait tout l’intérêt de l’application.',
    points: [
      { icon: 'wallet', label: 'Solde à ce jour', text: 'Ce que vous avez réellement, opérations passées uniquement.' },
      { icon: 'trendUp', label: 'Solde prévisionnel', text: 'Ce que vous aurez une fois vos opérations à venir passées.' },
      { icon: 'gauge', label: 'Seuils', text: 'Définissez un seuil d’alerte et un solde minimum : le tableau de bord vous situe en permanence par rapport à eux.' },
    ],
  },
]

const currencies = {
  'Europe': [
    { value: 'EUR', label: 'EUR — Euro (€)' },
    { value: 'GBP', label: 'GBP — Livre sterling (£)' },
    { value: 'CHF', label: 'CHF — Franc suisse (CHF)' },
    { value: 'SEK', label: 'SEK — Couronne suédoise (kr)' },
    { value: 'NOK', label: 'NOK — Couronne norvégienne (kr)' },
    { value: 'DKK', label: 'DKK — Couronne danoise (kr)' },
    { value: 'PLN', label: 'PLN — Złoty polonais (zł)' },
    { value: 'CZK', label: 'CZK — Couronne tchèque (Kč)' },
    { value: 'HUF', label: 'HUF — Forint hongrois (Ft)' },
    { value: 'RON', label: 'RON — Leu roumain (lei)' }
  ],
  'Amérique': [
    { value: 'USD', label: 'USD — Dollar américain ($)' },
    { value: 'CAD', label: 'CAD — Dollar canadien (C$)' },
    { value: 'MXN', label: 'MXN — Peso mexicain ($)' },
    { value: 'BRL', label: 'BRL — Real brésilien (R$)' },
    { value: 'ARS', label: 'ARS — Peso argentin ($)' },
    { value: 'CLP', label: 'CLP — Peso chilien ($)' },
    { value: 'COP', label: 'COP — Peso colombien ($)' }
  ],
  'Asie': [
    { value: 'JPY', label: 'JPY — Yen japonais (¥)' },
    { value: 'CNY', label: 'CNY — Yuan chinois (¥)' },
    { value: 'KRW', label: 'KRW — Won sud-coréen (₩)' },
    { value: 'INR', label: 'INR — Roupie indienne (₹)' },
    { value: 'SGD', label: 'SGD — Dollar de Singapour (S$)' },
    { value: 'HKD', label: 'HKD — Dollar de Hong Kong (HK$)' },
    { value: 'TWD', label: 'TWD — Dollar taïwanais (NT$)' },
    { value: 'THB', label: 'THB — Baht thaïlandais (฿)' },
    { value: 'MYR', label: 'MYR — Ringgit malaisien (RM)' },
    { value: 'IDR', label: 'IDR — Roupie indonésienne (Rp)' },
    { value: 'PHP', label: 'PHP — Peso philippin (₱)' },
    { value: 'VND', label: 'VND — Dong vietnamien (₫)' }
  ],
  'Afrique': [
    { value: 'ZAR', label: 'ZAR — Rand sud-africain (R)' },
    { value: 'EGP', label: 'EGP — Livre égyptienne (E£)' },
    { value: 'NGN', label: 'NGN — Naira nigérian (₦)' },
    { value: 'KES', label: 'KES — Shilling kényan (KSh)' },
    { value: 'GHS', label: 'GHS — Cedi ghanéen (GH₵)' },
    { value: 'MAD', label: 'MAD — Dirham marocain (DH)' },
    { value: 'TND', label: 'TND — Dinar tunisien (DT)' },
    { value: 'XOF', label: 'XOF — Franc CFA (CFA)' }
  ],
  'Océanie': [
    { value: 'AUD', label: 'AUD — Dollar australien (A$)' },
    { value: 'NZD', label: 'NZD — Dollar néo-zélandais (NZ$)' }
  ]
}

const TOTAL_STEPS = STEPS.length + 1 // les écrans de présentation + le choix de la devise

export default function Onboarding() {
  const [step, setStep] = useState(0)
  const [selectedDevise, setSelectedDevise] = useState('EUR')
  const [saving, setSaving] = useState(false)
  const { setOnboardingCompleted } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()

  const isLastStep = step === TOTAL_STEPS - 1

  const handleNext = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1))
  const handlePrev = () => setStep((s) => Math.max(s - 1, 0))

  const handleFinish = async () => {
    if (saving) return
    setSaving(true)
    try {
      await updateParametres({
        devise: selectedDevise,
        onboarding_completed: 1,
        decouvert_autorise: 0,
        montant_decouvert: 0,
        montant_seuil: 0,
        montant_minimum: 0,
        montant_plafond: 0
      })

      // Recharger les paramètres pour vérifier que la mise à jour a bien fonctionné
      const params = await getParametres()
      setOnboardingCompleted(params.onboarding_completed === 1)

      // Déclencher des événements pour forcer la mise à jour dans tous les composants
      window.dispatchEvent(new Event('onboardingCompleted'))
      window.dispatchEvent(new Event('paramsChanged'))

      // Rediriger vers la création du premier compte, puis le dashboard
      setTimeout(() => {
        navigate('/premier-compte', { replace: true })
      }, 300)
    } catch (error) {
      console.error('Erreur lors de la finalisation de l’onboarding:', error)
      // alert() gelait la page et sortait du design de l'application.
      toast.error(error.message || 'Erreur lors de la finalisation. Réessayez.')
      setSaving(false)
    }
  }

  const current = STEPS[step]

  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      <div className="flex flex-1 items-start justify-center px-4 py-10 sm:items-center">
        <div className="w-full max-w-xl">
          <div className="mb-7 flex flex-col items-center text-center">
            <Logo size={36} />
            <Wordmark className="mt-2.5 text-lg text-ink" />
          </div>

          <div className="card">
            <div className="min-h-[22rem] p-6 sm:p-8">
              {isLastStep ? (
                <>
                  <span className="mb-5 flex h-10 w-10 items-center justify-center rounded-full bg-accent-subtle text-accent">
                    <Icon name="wallet" size={20} />
                  </span>
                  <h1 className="text-2xl font-semibold text-ink">Choisissez votre devise</h1>
                  <p className="mt-1.5 text-base text-ink-secondary">
                    Elle s’appliquera à tous vos comptes et à toutes vos opérations.
                  </p>

                  <div className="field mt-6">
                    <label htmlFor="onboarding-devise" className="field-label">Devise</label>
                    <select
                      id="onboarding-devise"
                      value={selectedDevise}
                      onChange={(e) => setSelectedDevise(e.target.value)}
                      className="select"
                    >
                      {Object.entries(currencies).map(([region, list]) => (
                        <optgroup key={region} label={region}>
                          {list.map((currency) => (
                            <option key={currency.value} value={currency.value}>
                              {currency.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <p className="field-hint">
                      Vous pourrez en changer plus tard, mais tous vos montants seront alors
                      convertis.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <span className="overline">
                    Étape {step + 1} sur {TOTAL_STEPS}
                  </span>
                  <h1 className="mt-2 text-2xl font-semibold text-ink">{current.title}</h1>
                  <p className="mt-1.5 text-base text-ink-secondary">{current.lead}</p>

                  <ul className="mt-6 space-y-4">
                    {current.points.map((point) => (
                      <li key={point.label} className="flex gap-3.5">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-line bg-surface-sunken text-ink-secondary">
                          <Icon name={point.icon} size={17} />
                        </span>
                        <div className="min-w-0">
                          <p className="text-base font-medium text-ink">{point.label}</p>
                          <p className="mt-0.5 text-base text-ink-secondary">{point.text}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            <div className="card-footer flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={handlePrev}
                disabled={step === 0 || saving}
                className="btn-ghost"
              >
                <Icon name="chevronLeft" size={15} />
                Précédent
              </button>

              {/* Les points d'étape indiquent l'avancement ; ils ne sont pas
                  cliquables, l'ordre de lecture compte. */}
              <div className="flex items-center gap-1.5" aria-hidden="true">
                {Array.from({ length: TOTAL_STEPS }, (_, i) => (
                  <span
                    key={i}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      i === step ? 'w-5 bg-accent' : 'w-1.5 bg-line-strong'
                    }`}
                  />
                ))}
              </div>

              {isLastStep ? (
                <button type="button" onClick={handleFinish} disabled={saving} className="btn-primary">
                  {saving && <Icon name="refresh" size={15} className="animate-spin" />}
                  {saving ? 'Finalisation…' : 'Commencer'}
                </button>
              ) : (
                <button type="button" onClick={handleNext} className="btn-primary">
                  Suivant
                  <Icon name="chevronRight" size={15} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
